/**
 * PatientSelector — loads patients from Supabase and lets the user
 * pick recipients for a campaign via checkboxes.
 *
 * Props:
 *   onSelect(patients[])  – called immediately whenever selection changes.
 *                           Each element has: { id, name, phone, diagnosis,
 *                           preferred_language, hospital, status }
 *   title                 – optional header text (default "Select Patients")
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  IconCheckbox,
  IconCheckboxChecked,
  IconChevronDown,
  IconClose,
  IconPatientNew,
  IconPatients,
  IconSearch,
} from './icons';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';

// Language display helpers
const LANG_LABELS = {
  en: 'English',
  tw: 'Twi',
  ga: 'Ga',
  ee: 'Ewe',
  aka: 'Ga',
  dag: 'Dagbani',
  fat: 'Fante',
};
const langLabel = (code) => LANG_LABELS[code] || code || 'en';

const PatientSelector = ({ onSelect, title = 'Select Patients' }) => {
  const { user } = useAuth();

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterDiagnosis, setFilterDiagnosis] = useState('All');
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Load patients ────────────────────────────────────────────────────────────
  const loadPatients = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    try {
      // Match both lowercase canonical 'active' and legacy capitalised
      // 'Active'/'ACTIVE' rows so older imports aren't hidden from the
      // recipient list. The schema migration backfills these to lowercase.
      const { data } = await api.get('/patients', {
        params: { user_id: user.id },
      });
      const allPatients = data?.patients ?? data ?? [];
      const active = allPatients.filter((p) =>
        ['active', 'Active', 'ACTIVE'].includes(p.status)
      );
      setPatients(active);
    } catch (err) {
      setError('Failed to load patients: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPatients();
  }, [loadPatients]);

  // ── Derived lists ─────────────────────────────────────────────────────────
  const diagnoses = [
    'All',
    ...Array.from(new Set(patients.map((p) => p.diagnosis).filter(Boolean))).sort(),
  ];

  const filtered = patients.filter((p) => {
    if (filterDiagnosis !== 'All' && p.diagnosis !== filterDiagnosis) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (
        p.name?.toLowerCase().includes(q) ||
        p.phone?.includes(q) ||
        p.diagnosis?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const selectedPatients = patients.filter((p) => selectedIds.has(p.id));

  // ── Selection handlers ────────────────────────────────────────────────────
  const notify = (nextIds) => {
    onSelect(patients.filter((p) => nextIds.has(p.id)));
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
    notify(next);
  };

  const toggleAll = () => {
    const allFilteredSelected = filtered.every((p) => selectedIds.has(p.id));
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filtered.forEach((p) => next.delete(p.id));
    } else {
      filtered.forEach((p) => next.add(p.id));
    }
    setSelectedIds(next);
    notify(next);
  };

  const clearAll = () => {
    setSelectedIds(new Set());
    onSelect([]);
  };

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id));
  const someFilteredSelected = filtered.some((p) => selectedIds.has(p.id));

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <IconPatients className="w-4 h-4 text-primary-600" />
          <span className="text-sm font-semibold text-gray-700">{title}</span>
          {selectedIds.size > 0 && (
            <span className="bg-primary-100 text-primary-700 text-xs font-bold px-2 py-0.5 rounded-full">
              {selectedIds.size} selected
            </span>
          )}
        </div>
        {selectedIds.size > 0 && (
          <button
            type="button"
            onClick={clearAll}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors"
          >
            <IconClose className="w-3 h-3" />
            Clear selection
          </button>
        )}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="flex gap-2 px-4 py-2 border-b bg-white">
        {/* Search */}
        <div className="relative flex-1">
          <IconSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search name, phone or diagnosis…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400"
          />
        </div>

        {/* Diagnosis filter */}
        {diagnoses.length > 1 && (
          <div className="relative">
            <select
              value={filterDiagnosis}
              onChange={(e) => setFilterDiagnosis(e.target.value)}
              className="pl-2 pr-7 py-1.5 text-sm border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary-400 appearance-none"
            >
              {diagnoses.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
            <IconChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────────────────────── */}
      <div className="max-h-72 overflow-y-auto">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-400">
            <div className="h-5 w-5 border-2 border-gray-200 border-t-primary-500 rounded-full animate-spin mx-auto mb-2" />
            Loading patients…
          </div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-red-500 px-4">{error}</div>
        ) : patients.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-400 px-4">
            <IconPatients className="w-9 h-9 mx-auto mb-2 opacity-30" />
            <p>No patients found.</p>
            <p className="mt-1">
              <Link to="/patients" className="text-primary-600 hover:underline inline-flex items-center gap-1">
                <IconPatientNew className="w-3.5 h-3.5" />
                Add patients
              </Link>
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-8 text-center text-sm text-gray-400">
            No patients match your search.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 border-b z-10">
              <tr>
                <th className="w-10 px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="inline-flex items-center justify-center"
                    title={allFilteredSelected ? 'Deselect all' : 'Select all'}
                  >
                    {allFilteredSelected ? (
                      <IconCheckboxChecked className="w-4 h-4 text-primary-600" />
                    ) : someFilteredSelected ? (
                      <IconCheckboxChecked className="w-4 h-4 text-primary-300" />
                    ) : (
                      <IconCheckbox className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Name
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Phone
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">
                  Diagnosis
                </th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">
                  Language
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((p) => {
                const checked = selectedIds.has(p.id);
                return (
                  <tr
                    key={p.id}
                    onClick={() => toggleOne(p.id)}
                    className={`cursor-pointer transition-colors ${
                      checked
                        ? 'bg-primary-50 hover:bg-primary-100'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <td className="px-3 py-2 text-center">
                      {checked ? (
                        <IconCheckboxChecked className="w-4 h-4 text-primary-600 inline" />
                      ) : (
                        <IconCheckbox className="w-4 h-4 text-gray-300 inline" />
                      )}
                    </td>
                    <td className="px-3 py-2 font-medium text-gray-800">
                      {p.name}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500">
                      {p.phone}
                    </td>
                    <td className="px-3 py-2 text-gray-600 hidden sm:table-cell">
                      {p.diagnosis || (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell">
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {langLabel(p.preferred_language)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Footer summary ──────────────────────────────────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="px-4 py-2 bg-primary-50 border-t text-xs text-primary-700 flex flex-wrap gap-x-1">
          <span className="font-semibold">
            {selectedIds.size} patient{selectedIds.size !== 1 ? 's' : ''} selected:
          </span>
          <span className="truncate max-w-xs">
            {selectedPatients
              .slice(0, 4)
              .map((p) => p.name)
              .join(', ')}
            {selectedIds.size > 4 && ` +${selectedIds.size - 4} more`}
          </span>
        </div>
      )}
    </div>
  );
};

export default PatientSelector;
